import type { PageResourceData, SdkResourceField } from './types';

const RELATION_TYPE = 'relation';

type ResourceCatalog = Record<string, PageResourceData>;

function isRelationPointer(
  field: SdkResourceField
): field is SdkResourceField & { settings: { resource_id: string } } {
  return field.type === RELATION_TYPE && typeof field.settings?.resource_id === 'string';
}

/**
 * Build the nested field handles for a relation by reading the related resource
 * from the flat catalog and rewriting each field's `path`/`column_label` with the
 * relation prefix.
 */
function buildNestedFields(
  targetFields: Record<string, SdkResourceField>,
  parentPath: string,
  parentLabel: string,
  catalog: ResourceCatalog
): Record<string, SdkResourceField> {
  const nested: Record<string, SdkResourceField> = {};

  for (const [key, targetField] of Object.entries(targetFields)) {
    nested[key] = decorateField(
      {
        ...targetField,
        path: `${parentPath}.${targetField.key}`,
        column_label: `${parentLabel} ${targetField.label ?? targetField.key}`
      },
      catalog
    );
  }

  return nested;
}

/**
 * Returns a copy of `field` whose relation fields lazily expose the related
 * resource's fields (`family.fields.name`) and its configured `display_field_key`,
 * both read from the flat catalog. Non-relation fields are returned as-is.
 *
 * The `fields` accessor is resolved lazily and memoized; `display_field_key` reads
 * the related resource's configured key straight from the catalog. Both are
 * non-enumerable so spreads, `Object.keys`, and `JSON.stringify` never walk them
 * — this is what keeps a cyclic relationship graph from re-expanding eagerly.
 * Accessing `family.fields` builds only that one level; the nested relation
 * handles inside it stay lazy until they too are accessed.
 */
function decorateField(field: SdkResourceField, catalog: ResourceCatalog): SdkResourceField {
  if (!isRelationPointer(field)) {
    return field;
  }

  const decorated: SdkResourceField = { ...field };
  const targetResourceId = field.settings.resource_id;

  let cachedFields: Record<string, SdkResourceField> | undefined;
  const nestedFields = (): Record<string, SdkResourceField> => {
    if (cachedFields === undefined) {
      const target = catalog[targetResourceId];
      cachedFields = target
        ? buildNestedFields(target.fields, field.path, field.label ?? field.key, catalog)
        : {};
    }

    return cachedFields;
  };

  Object.defineProperty(decorated, 'fields', {
    enumerable: false,
    configurable: true,
    get: nestedFields
  });

  Object.defineProperty(decorated, 'display_field_key', {
    enumerable: false,
    configurable: true,
    get: () => catalog[targetResourceId]?.display_field_key ?? undefined
  });

  return decorated;
}

/**
 * Decorate a resource's top-level fields so relation fields dereference their
 * nested fields from the flat `catalog` on demand. The catalog is the raw
 * `resources` prop (plain data, never itself decorated) and is the single
 * source the accessors read from.
 */
export function decorateResourceFields(
  fields: Record<string, SdkResourceField>,
  catalog: ResourceCatalog
): Record<string, SdkResourceField> {
  const decorated: Record<string, SdkResourceField> = {};

  for (const [key, field] of Object.entries(fields)) {
    decorated[key] = decorateField(field, catalog);
  }

  return decorated;
}
